import { useAppIndexStore } from '@base/stores/indexStore'
import type * as tp from '@base/utils/types'
import {
  ut_convertRouterParamsToURL,
  ut_getCartType,
  ut_getLocalStorage,
  ut_getLocalStorageKey,
  ut_isEmbedded,
  ut_isPrint,
  ut_returnNewRoute,
} from '@base/utils/utils'
import type { ECarts, ESpecialPage } from '@base/utils/constants'
import { ElocalStorageType } from '@base/utils/constants'
import { useCartStore } from '@base/stores/cartStore'
import type { Messages } from '@nuxt/ui'
import { defineLocale } from '@nuxt/ui/composables/defineLocale'

export function useAppIndex() {
  const indexStore = useAppIndexStore()
  const cartStore = useCartStore()
  const appConfig = useAppConfig()
  const loadingIndicator = useLoadingIndicator()
  const runtimeConfig = useRuntimeConfig()
  const storageHandler = (e: StorageEvent) => updateStore(e.key || '', e.newValue || '')

  onMounted(async () => {
    if (import.meta.client) {
      if (appConfig.useLocalStorage) {
        window.addEventListener('storage', storageHandler)
        // Initialize store only once
        if (!indexStore.state.cartIsInitialized) {
          indexStore.setCartIsInitialized(true)
          await initializeStore()
        }
        else {
          // Update cart from server
          await updateCart()
        }
      }
    }
  })

  onBeforeUnmount(() => {
    if (import.meta.client) {
      if (appConfig.useLocalStorage) {
        window.removeEventListener('storage', storageHandler)
      }
    }
  })

  // Watch loading state
  watch(
    () => indexStore.state.isLoading,
    () => {
      if (indexStore.state.isLoading) {
        loadingIndicator.start({ force: true })
      }
      else {
        loadingIndicator.finish({ force: true })
      }
    },
  )

  useHead({
    title: computed(() => (indexStore.state.headElements.title || indexStore.state.currentNode.desc) + (appConfig.titleSeparator || '') + (appConfig.companyDesc || '')),
    htmlAttrs: {
      lang: indexStore.state.paramData.lcda,
    },
    meta: [
      { name: 'description', content: computed(() => ((indexStore.state.headElements.description || indexStore.state.currentNode.desc))) },
      { name: 'keywords', content: computed(() => ((indexStore.state.headElements.keywords || ''))) },
      { property: 'og:url', content: indexStore.getAbsolutePath },
      { property: 'og:title', content: computed(() => (indexStore.state.openGraphElements.title || indexStore.state.currentNode.desc)) },
      { property: 'og:description', content: computed(() => (indexStore.state.openGraphElements.description || indexStore.state.currentNode.desc)) },
      { property: 'og:image', content: computed(() => (indexStore.state.openGraphElements.imageUrl || '')) },
      /* { property: 'og:site_name', content: indexStore.state.openGraphElements.sitename ? indexStore.state.openGraphElements.sitename : indexStore.state.paramData.embeddedInfo.embedded ? 'https://' + indexStore.state.paramData.embeddedInfo.hostname : process.env.DATAHOST }, */
    ],
    script: [
      { type: 'application/ld+json', textContent: computed(() => (JSON.stringify(indexStore.getJsonLD))) },
    ],
  })

  const locale = defineLocale<Messages>({
    name: '',
    code: indexStore.state.paramData.lcda.replace('en_us', 'en'), // Customer specific replacement of not official languages, should be done better in future
    // @ts-expect-error no messages defined only code is used
    messages: {},
  })

  // Use login screen
  const grantedAccess = computed((): boolean => {
    return (indexStore.state.accessGranted || import.meta.env.DEV || ut_isEmbedded() || ut_isPrint())
  })

  // display raw url
  const rawurl = computed((): string => {
    let res: string = ''
    if (import.meta.client) {
      const newParam: tp.IParamRouter = ut_returnNewRoute('', {}, '')
      newParam.path = window.location.origin + (runtimeConfig.public.baseURL || '') + indexStore.getCountryLangPath + '/' + indexStore.state.currentNode.namePath
      newParam.query.rawurl = '1'
      res = ut_convertRouterParamsToURL(newParam)
    }
    return res
  })

  async function initializeStore(): Promise<void> {
    await updateStore(ut_getLocalStorageKey(ElocalStorageType.ls_shoppingcart), ut_getLocalStorage(ElocalStorageType.ls_shoppingcart))
    await updateStore(ut_getLocalStorageKey(ElocalStorageType.ls_userinfo), ut_getLocalStorage(ElocalStorageType.ls_userinfo))
  }

  async function updateStore(key: string, value: string): Promise<void> {
    let json = []
    try {
      json = value ? JSON.parse(value || '{}') : {}
    }
    catch (err) {
      const e = err as Error
      console.error('Error updating store', e.message, key, value)
    }

    switch (key) {
      case ut_getLocalStorageKey(ElocalStorageType.ls_shoppingcart): {
        /*
         * ⚑ This runs for the page load AND for every storage event from another tab — the handler
         * below is registered on both. Carts listed in `tabLocalCarts` are therefore left alone by
         * `setWholeCart` itself, in the store, rather than being emptied here.
         *
         * Emptying them here is what the previous version did (`transientCarts`, 24.08.2026), and it
         * was right for the page load and wrong for the other caller: any second tab that wrote its
         * cart emptied the running tab's price list, mid-edit (31.08.2026).
         */
        cartStore.setWholeCart(json)
        // Update cart from server
        await updateCart()
        break
      }
      case ut_getLocalStorageKey(ElocalStorageType.ls_userinfo): {
        for (const [key, value] of Object.entries(json)) {
          indexStore.setUserDefined(key, value)
        }
        break
      }
    }
  }

  async function updateCart(): Promise<void> {
    if (indexStore.getIsCartPage) {
      const specialPage: Partial<ESpecialPage> | undefined = indexStore.state.currentNode.nodeName as ESpecialPage
      if (specialPage) {
        const cart: Partial<ECarts> | undefined = ut_getCartType(specialPage)
        if (cart) {
          await cartStore.setUpdateCart(cart)
        }
      }
    }
  }

  return {
    indexStore,
    cartStore,
    grantedAccess,
    rawurl,
    locale,
  }
}
